import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GoogleMapsModule } from '@angular/google-maps';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatPseudoCheckboxModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { CountriesModule } from '@qaroni-app/countries/countries.module';
import { RoleAccessPermissionDirective } from '@qaroni-core/directives/role-access-permission/role-access-permission.directive';
import { DirectivesModule } from '@qaroni-shared/directives/directives.module';
import { PipesModule } from '@qaroni-shared/pipes/pipes.module';
import { QaroniAnimationModule } from '@qaroni-shared/qaroni-animation/qaroni-animation.module';
import { EditorModule, TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';
import { AbsenceConfirmationPreviewComponent } from './absence-confirmation-preview/absence-confirmation-preview.component';
import { AbsenceEmployeeStatusPreviewComponent } from './absence-employee-status-preview/absence-employee-status-preview.component';
import { AbsenceStatusPreviewComponent } from './absence-status-preview/absence-status-preview.component';
import { AbsenceTypePreviewComponent } from './absence-type-preview/absence-type-preview.component';
import { AbsenceTypeDetailsComponent } from './absence-type-details/absence-type-details.component';
import { AbsenceGrantsAlertComponent } from './absence-grants-alert/absence-grants-alert.component';
import { AbsenceEmployeeStatusChipComponent } from './absence-employee-status-chip/absence-employee-status-chip.component';
import { AbsenceEmployeeSummaryComponent } from './absence-employee-summary/absence-employee-summary.component';
import { AbsenceEmployeeWarningsComponent } from './absence-employee-warnings/absence-employee-warnings.component';
import { AbsencesUnitSelectComponent } from './absences-unit-select/absences-unit-select.component';
import { AbsencesUnitsPreviewComponent } from './absences-units-preview/absences-units-preview.component';
import { AddressStringComponent } from './address-string/address-string.component';
import { AdvancedFiltersButtonComponent } from './advanced-filters-button/advanced-filters-button.component';
import { AdvancedFiltersContainerComponent } from './advanced-filters-container/advanced-filters-container.component';
import { AllowanceProgressBarPreviewComponent } from './allowance-progress-bar-preview/allowance-progress-bar-preview.component';
import { AssessmentDoughnutChartPreviewComponent } from './assessment-doughnut-chart-preview/assessment-doughnut-chart-preview.component';
import { AssessmentGaugeChartPreviewComponent } from './assessment-gauge-chart-preview/assessment-gauge-chart-preview.component';
import { AssessmentProgressBarPreviewComponent } from './assessment-progress-bar-preview/assessment-progress-bar-preview.component';
import { BoardSectionAvatarPreviewComponent } from './board-section-avatar-preview/board-section-avatar-preview.component';
import { BoardSectionLoadingComponent } from './board-section-loading/board-section-loading.component';
import { CalendarDayDetailsDialogComponent } from './calendar-day-details-dialog/calendar-day-details-dialog.component';
import { CardCompanyComponent } from './card-company/card-company.component';
import { CardWorkplaceComponent } from './card-workplace/card-workplace.component';
import { CheckDetailsCalendarComponent } from './check-details-calendar/check-details-calendar.component';
import { CheckDetailsDayAbsenceChipComponent } from './check-details-day-absence-chip/check-details-day-absence-chip.component';
import { CheckDetailsHoursComponent } from './check-details-hours/check-details-hours.component';
import { CheckDetailsLeadComponent } from './check-details-lead/check-details-lead.component';
import { CheckHistoryDialogComponent } from './check-history-dialog/check-history-dialog.component';
import { CheckRequestsStatusPreviewComponent } from './check-requests-status-preview/check-requests-status-preview.component';
import { CheckRequestsTypePreviewComponent } from './check-requests-type-preview/check-requests-type-preview.component';
import { ChecklistsListGlobalFilterComponent } from './checklists-list-global-filter/checklists-list-global-filter.component';
import { ComeBackButtonComponent } from './come-back-button/come-back-button.component';
import { DepartmentsFilesPreviewComponent } from './departments-files-preview/departments-files-preview.component';
import { DepartmentsFilterComponent } from './departments-filter/departments-filter.component';
import { AssessmentResultsCommentsRegisterComponent } from './dialogs/assessment-results-comments-register/assessment-results-comments-register.component';
import { CheckCommentDialogComponent } from './dialogs/check-comment-dialog/check-comment-dialog.component';
import { ConfirmationDialogComponent } from './dialogs/confirmation-dialog/confirmation-dialog.component';
import { ErrorsHttpDialogComponent } from './dialogs/errors-http-dialog/errors-http-dialog.component';
import { FileViewerDialogComponent } from './dialogs/file-viewer-dialog/file-viewer-dialog.component';
import { ForgotPasswordDialogComponent } from './dialogs/forgot-password-dialog/forgot-password-dialog.component';
import { SendInvitationDialogComponent } from './dialogs/send-invitation-dialog/send-invitation-dialog.component';
import { TagsMenuUpdateDialogComponent } from './dialogs/tags-menu-update-dialog/tags-menu-update-dialog.component';
import { WorkplaceAddDialogComponent } from './dialogs/workplace-add-dialog/workplace-add-dialog.component';
import { DurationInputComponent } from './duration-input/duration-input.component';
import { EmployeeDisabilitiesTableComponent } from './employee-disabilities-table/employee-disabilities-table.component';
import { EmployeeProfileAddressPreviewComponent } from './employee-profile-address-preview/employee-profile-address-preview.component';
import { EmployeeProfileContractPreviewComponent } from './employee-profile-contract-preview/employee-profile-contract-preview.component';
import { EmployeesFilterComponent } from './employees-filter/employees-filter.component';
import { EmptyResultPreviewComponent } from './empty-result-preview/empty-result-preview.component';
import { ExpirationDaysPreviewComponent } from './expiration-days-preview/expiration-days-preview.component';
import { GeneralBalanceComponent } from './general-balance/general-balance.component';
import { GrecaptchaComponent } from './grecaptcha/grecaptcha.component';
import { HolidaysDetailsSnackbarComponent } from './holidays-details-snackbar/holidays-details-snackbar.component';
import { HoursBalanceChartPreviewComponent } from './hours-balance-chart-preview/hours-balance-chart-preview.component';
import { ImputationHoursChartPreviewComponent } from './imputation-hours-chart-preview/imputation-hours-chart-preview.component';
import { InputDateComponent } from './input-date/input-date.component';
import { InputErrorMessagesComponent } from './input-error-messages/input-error-messages.component';
import { InputLabelComponent } from './input-label/input-label.component';
import { LabelAvatarPreviewComponent } from './label-avatar-preview/label-avatar-preview.component';
import { LabelEmailPreviewComponent } from './label-email-preview/label-email-preview.component';
import { LabelPhonePreviewComponent } from './label-phone-preview/label-phone-preview.component';
import { LabelProjectNamePreviewComponent } from './label-project-name-preview/label-project-name-preview.component';
import { LabelTextPreviewComponent } from './label-text-preview/label-text-preview.component';
import { LineChartPreviewComponent } from './line-chart-preview/line-chart-preview.component';
import { LoadingComponent } from './loading/loading.component';
import { MatrixNineGridBoxEmployeeComponent } from './matrix-nine-grid-box-employee/matrix-nine-grid-box-employee.component';
import { MatrixNineGridBoxComponent } from './matrix-nine-grid-box/matrix-nine-grid-box.component';
import { MatrixNineGridComponent } from './matrix-nine-grid/matrix-nine-grid.component';
import { MenuCompanyComponent } from './menu-company/menu-company.component';
import { MenuComponent } from './menu/menu.component';
import { MonthDayDatepickerComponent } from './month-day-datepicker/components/month-day-datepicker.component';
import { MonthDayInputComponent } from './month-day-input/month-day-input.component';
import { PaymentMethodPreviewComponent } from './payment-method-preview/payment-method-preview.component';
import { PaymentsCardInfoComponent } from './payments-card-info/payments-card-info.component';
import { PaymentsCardSubscribeButtonComponent } from './payments-card-subscribe-button/payments-card-subscribe-button.component';
import { PaymentsCardComponent } from './payments-card/payments-card.component';
import { PaymentsLevelContainerComponent } from './payments-level-container/payments-level-container.component';
import { PaymentsLevelLineComponent } from './payments-level-line/payments-level-line.component';
import { PaymentsLevelComponent } from './payments-level/payments-level.component';
import { PermitsEmployeesListTableFilesPreviewComponent } from './permits-employees-list-table-files-preview/permits-employees-list-table-files-preview.component';
import { PieChartPreviewComponent } from './pie-chart-preview/pie-chart-preview.component';
import { ProfileStatusProgressbarComponent } from './profile-status-progressbar/profile-status-progressbar.component';
import { ProgressSpinnerComponent } from './progress-spinner/progress-spinner.component';
import { ProjectPreviewStatusComponent } from './project-preview-status/project-preview-status.component';
import { ProjectsTasksTagsComponent } from './projects-tasks-tags/projects-tasks-tags.component';
import { QueryParamSearchFilterComponent } from './query-param-search-filter/query-param-search-filter.component';
import { QueryParamSelectEnumFilterComponent } from './query-param-select-enum-filter/query-param-select-enum-filter.component';
import { QueryParamSelectObjectFilterComponent } from './query-param-select-object-filter/query-param-select-object-filter.component';
import { RangeDatepickerComponent } from './range-datepicker/range-datepicker.component';
import { RedirectLinkComponent } from './redirect-link/redirect-link.component';
import { ResultCommentItemComponent } from './result-comment-item/result-comment-item.component';
import { ResultGraphicsCanvasComponent } from './result-graphics-canvas/result-graphics-canvas.component';
import { ScoreboardDetailPreviewComponent } from './scoreboard-detail-preview/scoreboard-detail-preview.component';
import { SelectEmployeeFormFieldComponent } from './select-employee-form-field/select-employee-form-field.component';
import { SelectUserFormFieldComponent } from './select-user-form-field/select-user-form-field.component';
import { ShiftStatusPreviewComponent } from './shift-status-preview/shift-status-preview.component';
import { StatusPreviewComponent } from './status-preview/status-preview.component';
import { TaskPreviewStatusComponent } from './task-preview-status/task-preview-status.component';
import { TimeBankSummariesComponent } from './time-bank-summaries/time-bank-summaries.component';
import { TinymceTextareaComponent } from './tinymce-textarea/tinymce-textarea.component';
import { TitleButtonComponent } from './title-button/title-button.component';
import { TitleDashboardComponent } from './title-dashboard/title-dashboard.component';
import { TitleMdComponent } from './title-md/title-md.component';
import { TitleStatusChipComponent } from './title-status-chip/title-status-chip.component';
import { TitleSubtitleComponent } from './title-subtitle/title-subtitle.component';
import { TitleComponent } from './title/title.component';
import { ToggleViewComponent } from './toggle-view/toggle-view.component';
import { UploadFilesItemComponent } from './upload-files-item/upload-files-item.component';
import { UploadFilesComponent } from './upload-files/upload-files.component';
import { WeekDatepickerComponent } from './week-datepicker/week-datepicker.component';
import { WorkplaceFilterComponent } from './workplace-filter/workplace-filter.component';
import { WorkplacesMapComponent } from './workplaces-map/workplaces-map.component';
import { YearDatepickerComponent } from './year-datepicker/year-datepicker.component';
import { YearMonthDatepickerComponent } from './year-month-datepicker/year-month-datepicker.component';

const components = [
  AbsenceConfirmationPreviewComponent,
  AbsenceEmployeeStatusChipComponent,
  AbsenceEmployeeStatusPreviewComponent,
  AbsenceEmployeeSummaryComponent,
  AbsenceEmployeeWarningsComponent,
  AbsenceGrantsAlertComponent,
  AbsenceTypeDetailsComponent,
  AbsenceStatusPreviewComponent,
  AbsencesUnitSelectComponent,
  AbsencesUnitsPreviewComponent,
  AbsenceTypePreviewComponent,
  AddressStringComponent,
  AdvancedFiltersButtonComponent,
  AdvancedFiltersContainerComponent,
  AllowanceProgressBarPreviewComponent,
  AssessmentDoughnutChartPreviewComponent,
  AssessmentGaugeChartPreviewComponent,
  AssessmentProgressBarPreviewComponent,
  AssessmentResultsCommentsRegisterComponent,
  BoardSectionAvatarPreviewComponent,
  BoardSectionLoadingComponent,
  CalendarDayDetailsDialogComponent,
  CardCompanyComponent,
  CardWorkplaceComponent,
  CheckCommentDialogComponent,
  CheckDetailsCalendarComponent,
  CheckDetailsDayAbsenceChipComponent,
  CheckDetailsHoursComponent,
  CheckDetailsLeadComponent,
  CheckHistoryDialogComponent,
  ChecklistsListGlobalFilterComponent,
  CheckRequestsStatusPreviewComponent,
  CheckRequestsTypePreviewComponent,
  ComeBackButtonComponent,
  ConfirmationDialogComponent,
  DepartmentsFilesPreviewComponent,
  DepartmentsFilterComponent,
  DurationInputComponent,
  EmployeeDisabilitiesTableComponent,
  EmployeeProfileAddressPreviewComponent,
  EmployeeProfileContractPreviewComponent,
  EmployeesFilterComponent,
  EmptyResultPreviewComponent,
  ErrorsHttpDialogComponent,
  ExpirationDaysPreviewComponent,
  FileViewerDialogComponent,
  ForgotPasswordDialogComponent,
  GeneralBalanceComponent,
  GrecaptchaComponent,
  HolidaysDetailsSnackbarComponent,
  HoursBalanceChartPreviewComponent,
  ImputationHoursChartPreviewComponent,
  InputDateComponent,
  InputErrorMessagesComponent,
  InputLabelComponent,
  LabelAvatarPreviewComponent,
  LabelEmailPreviewComponent,
  LabelPhonePreviewComponent,
  LabelProjectNamePreviewComponent,
  LabelTextPreviewComponent,
  LineChartPreviewComponent,
  LoadingComponent,
  MatrixNineGridBoxComponent,
  MatrixNineGridBoxEmployeeComponent,
  MatrixNineGridComponent,
  MenuCompanyComponent,
  MenuComponent,
  MonthDayDatepickerComponent,
  MonthDayInputComponent,
  PaymentMethodPreviewComponent,
  PaymentsCardComponent,
  PaymentsCardInfoComponent,
  PaymentsCardSubscribeButtonComponent,
  PaymentsLevelComponent,
  PaymentsLevelContainerComponent,
  PaymentsLevelLineComponent,
  PermitsEmployeesListTableFilesPreviewComponent,
  PieChartPreviewComponent,
  ProfileStatusProgressbarComponent,
  ProgressSpinnerComponent,
  ProjectPreviewStatusComponent,
  ProjectsTasksTagsComponent,
  QueryParamSearchFilterComponent,
  QueryParamSelectEnumFilterComponent,
  QueryParamSelectObjectFilterComponent,
  RangeDatepickerComponent,
  RedirectLinkComponent,
  ResultCommentItemComponent,
  ResultGraphicsCanvasComponent,
  ScoreboardDetailPreviewComponent,
  SelectEmployeeFormFieldComponent,
  SelectUserFormFieldComponent,
  SendInvitationDialogComponent,
  ShiftStatusPreviewComponent,
  StatusPreviewComponent,
  TagsMenuUpdateDialogComponent,
  TaskPreviewStatusComponent,
  TimeBankSummariesComponent,
  TinymceTextareaComponent,
  TitleButtonComponent,
  TitleComponent,
  TitleDashboardComponent,
  TitleMdComponent,
  TitleStatusChipComponent,
  TitleSubtitleComponent,
  ToggleViewComponent,
  UploadFilesComponent,
  UploadFilesItemComponent,
  WeekDatepickerComponent,
  WorkplaceAddDialogComponent,
  WorkplaceFilterComponent,
  WorkplacesMapComponent,
  YearDatepickerComponent,
  YearMonthDatepickerComponent,
];

@NgModule({
  declarations: [components],
  imports: [
    CommonModule,
    CountriesModule,
    DirectivesModule,
    EditorModule,
    FormsModule,
    GoogleMapsModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCardModule,
    MatDatepickerModule,
    MatDialogModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatMenuModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatPseudoCheckboxModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatSortModule,
    MatTableModule,
    MatTooltipModule,
    OverlayModule,
    PipesModule,
    QaroniAnimationModule,
    ReactiveFormsModule,
    RoleAccessPermissionDirective,
    RouterModule,
  ],
  exports: [components],
  providers: [
    { provide: TINYMCE_SCRIPT_SRC, useValue: 'tinymce/tinymce.min.js' },
  ],
})
export class ComponentsModule {}
